import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Autocomplete from './ui/autocomplete';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  User, 
  FileText, 
  BarChart3, 
  Target, 
  Users, 
  Settings, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Link,
  Copy,
  Plus,
  Calendar,
  Eye,
  Trash2,
  Lightbulb,
  Shield,
  Zap,
  Heart,
  Activity,
  Download,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';


const API = process.env.REACT_APP_BACKEND_URL;

// Normalizes text for search: lowercase and strips accents/special characters
const normalizeSearchText = (text) => {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
};

const DISCAssessmentPage = () => {
  const { user } = useAuth();
  const ALL_TABS = ['new', 'description', 'history', 'reports', 'public-links'];
  const allowedTabs = ALL_TABS;
  const [currentTab, setCurrentTab] = useState(allowedTabs.includes('new') ? 'new' : (allowedTabs[0] || 'new'));
  // States
  const [questions, setQuestions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [publicLinks, setPublicLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Assessment states
  const [selectedEmployee, setSelectedEmployee] = useState('none');
  const [participantName, setParticipantName] = useState('');
  const [participantEmail, setParticipantEmail] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [currentAssessmentId, setCurrentAssessmentId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // History filters
  const [historyNameFilter, setHistoryNameFilter] = useState('');
  const [historyDateFilter, setHistoryDateFilter] = useState('');
  const filteredHistoryAssessments = assessments.filter(assessment => {
    const nameMatch = !historyNameFilter || normalizeSearchText(assessment.participant_name).includes(normalizeSearchText(historyNameFilter));
    const dateMatch = !historyDateFilter || new Date(assessment.assessment_date).toISOString().slice(0, 10) === historyDateFilter;
    return nameMatch && dateMatch;
  });

  // Likert scale options
  const likertOptions = [
    { value: 1, label: 'Discordo Totalmente' },
    { value: 2, label: 'Discordo' },
    { value: 3, label: 'Neutro' },
    { value: 4, label: 'Concordo' },
    { value: 5, label: 'Concordo Totalmente' }
  ];

  // Load initial data
  useEffect(() => {
    fetchQuestions();
    fetchEmployees();
    fetchAssessments();
    fetchPublicLinks();
  }, []);

  // Update participant name when employee is selected
  useEffect(() => {
    if (selectedEmployee && selectedEmployee !== 'none' && employees.length > 0) {
      const employee = employees.find(emp => emp.id === selectedEmployee);
      if (employee) {
        setParticipantName(employee.nome);
        setParticipantEmail(employee.email || '');
      }
    } else if (selectedEmployee === 'none') {
      setParticipantName('');
      setParticipantEmail('');
    }
  }, [selectedEmployee, employees]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/api/disc/questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Erro ao carregar perguntas');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(`${API}/api/disc/assessments`);
      setAssessments(response.data);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  };

  const fetchPublicLinks = async () => {
    try {
      const response = await axios.get(`${API}/api/disc/public-links`);
      setPublicLinks(response.data);
    } catch (error) {
      console.error('Error fetching public links:', error);
    }
  };

  const createPublicLink = async (linkData) => {
    try {
      const response = await axios.post(`${API}/api/disc/public-link`, linkData);
      toast.success('Link público criado com sucesso!');
      fetchPublicLinks(); // Refresh the list
      return response.data;
    } catch (error) {
      console.error('Error creating public link:', error);
      toast.error('Erro ao criar link público');
      throw error;
    }
  };

  const deletePublicLink = async (linkId) => {
    try {
      await axios.delete(`${API}/api/disc/public-link/${linkId}`);
      toast.success('Link público removido com sucesso!');
      fetchPublicLinks(); // Refresh the list
    } catch (error) {
      console.error('Error deleting public link:', error);
      toast.error('Erro ao remover link público');
    }
  };

  const deleteAssessment = async (assessmentId, participantName) => {
    if (!window.confirm(`Tem certeza que deseja excluir a avaliação de "${participantName}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/api/disc/assessment/${assessmentId}`);
      toast.success('Avaliação removida com sucesso!');
      fetchAssessments();
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Erro ao remover avaliação');
    }
  };

  const downloadReportPDF = async (assessmentId) => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        `${API}/api/disc/assessment/${assessmentId}/pdf`,
        { responseType: 'blob' }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get participant name for filename
      const assessment = assessments.find(a => a.id === assessmentId);
      const filename = `DISC_${assessment?.participant_name || 'Relatorio'}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório PDF baixado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast.error('Não foi possível baixar o relatório PDF');
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = async () => {
    if (!participantName.trim()) {
      toast.error('Por favor, informe o nome do participante');
      return;
    }

    if (participantEmail.trim()) {
      try {
        setLoading(true);
        const response = await axios.post(`${API}/api/disc/assessment/start`, {
          participant_name: participantName.trim(),
          participant_email: participantEmail.trim()
        });
        if (response.data.email_sent) {
          toast.success(`Link enviado com sucesso para ${participantEmail}`);
        } else {
          toast.error(`Link criado, mas não foi possível enviar o email para ${participantEmail}. Copie o link manualmente na aba Links Públicos.`);
        }
        setAssessmentStarted(false);
      } catch (error) {
        console.error('Error creating assessment link:', error);
        toast.error('Não foi possível criar o link da avaliação');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setAssessmentStarted(true);
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  const handleAnswerChange = (questionId, score) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: parseInt(score)
    }));
  };

  const nextQuestion = () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (!answers[currentQuestion.id]) {
      toast.error('Por favor, selecione uma resposta');
      return;
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      submitAssessment();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const submitAssessment = async () => {
    setLoading(true);
    
    try {
      const assessmentAnswers = Object.entries(answers).map(([questionId, score]) => ({
        question_id: questionId,
        score: score
      }));

      const assessmentData = {
        employee_id: (selectedEmployee && selectedEmployee !== 'none') ? selectedEmployee : null,
        participant_name: participantName,
        participant_email: participantEmail,
        answers: assessmentAnswers
      };

      const response = await axios.post(`${API}/api/disc/assessment`, assessmentData);
      
      setCurrentAssessmentId(response.data.assessment_id);
      setAssessmentCompleted(true);
      toast.success('Avaliação DISC concluída com sucesso!');
      
      // Refresh assessments list
      fetchAssessments();
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Erro ao salvar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const resetAssessment = () => {
    setAssessmentStarted(false);
    setAssessmentCompleted(false);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setSelectedEmployee('none');
    setParticipantName('');
    setParticipantEmail('');
    setCurrentAssessmentId(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStyleBadgeColor = (style) => {
    const colors = {
      'D': 'bg-red-100 text-red-800',
      'I': 'bg-yellow-100 text-yellow-800', 
      'S': 'bg-green-100 text-green-800',
      'C': 'bg-blue-100 text-blue-800'
    };
    return colors[style] || 'bg-gray-100 text-gray-800';
  };

  const getStyleName = (style) => {
    const names = {
      'D': 'Dominante',
      'I': 'Influente',
      'S': 'Estável', 
      'C': 'Consciente'
    };
    return names[style] || style;
  };

  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Avaliação DISC</h1>
        <p className="text-gray-600">
          Metodologia William Moulton Marston - Análise de Perfil Comportamental
        </p>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          {allowedTabs.includes('new') && <TabsTrigger value="new">Nova Avaliação</TabsTrigger>}
          {allowedTabs.includes('description') && <TabsTrigger value="description">Descrição</TabsTrigger>}
          {allowedTabs.includes('history') && <TabsTrigger value="history">Histórico</TabsTrigger>}
          {allowedTabs.includes('reports') && <TabsTrigger value="reports">Relatórios</TabsTrigger>}
          {allowedTabs.includes('public-links') && <TabsTrigger value="public-links">Links Públicos</TabsTrigger>}
        </TabsList>

        {/* New Assessment Tab */}
        {allowedTabs.includes('new') && <TabsContent value="new">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Nova Avaliação DISC</span>
              </CardTitle>
              <CardDescription>
                Questionário com 48 perguntas baseado na metodologia Marston
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!assessmentStarted && !assessmentCompleted && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="employee">Colaborador (Opcional)</Label>
                        <Autocomplete
                          value={selectedEmployee}
                          onValueChange={setSelectedEmployee}
                          options={[{ id: 'none', nome: 'Não vincular a funcionário' }, ...employees]}
                          getOptionValue={(employee) => employee.id}
                          getOptionLabel={(employee) => employee.nome}
                          placeholder="Selecione um funcionário ou deixe em branco"
                          emptyMessage="Nenhum funcionário encontrado"
                        />
                      </div>

                      <div>
                        <Label htmlFor="participant_name">Nome do Participante *</Label>
                        <Input
                          id="participant_name"
                          value={participantName}
                          onChange={(e) => setParticipantName(e.target.value.replace(/\d/g, ''))}
                          placeholder="Digite o nome completo"
                          disabled={selectedEmployee && selectedEmployee !== 'none'}
                        />
                      </div>

                      <div>
                        <Label htmlFor="participant_email">E-mail (Opcional)</Label>
                        <Input
                          id="participant_email"
                          type="email"
                          value={participantEmail}
                          onChange={(e) => setParticipantEmail(e.target.value)}
                          placeholder="participante@exemplo.com"
                        />
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-medium text-blue-900 mb-2">Sobre a Avaliação DISC</h3>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• <strong>48 perguntas</strong> baseadas na metodologia Marston</li>
                        <li>• <strong>Escala Likert</strong> de 1 a 5 pontos</li>
                        <li>• <strong>4 perfis</strong>: Dominante, Influente, Estável, Consciente</li>
                        <li>• <strong>Tempo estimado</strong>: 15-20 minutos</li>
                        <li>• <strong>Relatório detalhado</strong> com recomendações</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={startAssessment} className="px-8">
                      Iniciar Avaliação
                    </Button>
                  </div>
                </div>
              )}

              {assessmentStarted && !assessmentCompleted && questions.length > 0 && (
                <div className="space-y-6">
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Progresso</span>
                      <span>{currentQuestionIndex + 1} de {questions.length}</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  {/* Question */}
                  <Card className="bg-gray-50">
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">
                            Pergunta {currentQuestionIndex + 1}
                          </Badge>
                        </div>
                        
                        <h3 className="text-lg font-medium text-gray-900">
                          {questions[currentQuestionIndex].question}
                        </h3>

                        <RadioGroup
                          value={answers[questions[currentQuestionIndex].id]?.toString() || ''}
                          onValueChange={(value) => handleAnswerChange(questions[currentQuestionIndex].id, value)}
                        >
                          {likertOptions.map(option => (
                            <div key={option.value} className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100">
                              <RadioGroupItem value={option.value.toString()} id={`option-${option.value}`} />
                              <Label htmlFor={`option-${option.value}`} className="flex-grow cursor-pointer">
                                <span className="font-medium">{option.value}</span> - {option.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Navigation */}
                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={previousQuestion}
                      disabled={currentQuestionIndex === 0}
                    >
                      Anterior
                    </Button>
                    
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        Cancelar
                      </Button>
                      
                      <Button
                        onClick={nextQuestion}
                        disabled={!answers[questions[currentQuestionIndex].id]}
                        loading={loading}
                      >
                        {currentQuestionIndex === questions.length - 1 ? 'Finalizar' : 'Próxima'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {assessmentCompleted && (
                <div className="text-center space-y-6">
                  <div className="max-w-md mx-auto">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Avaliação Concluída!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Sua avaliação DISC foi processada com sucesso. Você pode visualizar o relatório detalhado na aba "Relatórios".
                    </p>
                  </div>

                  <div className="flex justify-center space-x-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentTab('reports')}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Ver Relatório
                    </Button>
                    
                    <Button onClick={resetAssessment}>
                      <User className="h-4 w-4 mr-2" />
                      Nova Avaliação
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Description Tab */}
        {allowedTabs.includes('description') && <TabsContent value="description">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Lightbulb className="h-6 w-6 text-blue-600" />
                Sobre a Avaliação DISC
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Metodologia */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-blue-900 mb-3">📋 Metodologia DISC</h3>
                <div className="space-y-3 text-gray-700">
                  <p>
                    A <strong>Avaliação DISC</strong> é uma ferramenta de análise comportamental baseada na teoria desenvolvida 
                    por <strong>William Moulton Marston</strong> na década de 1920, descrita em seu livro 
                    "Emotions of Normal People" (Emoções de Pessoas Normais).
                  </p>
                  <p>
                    O modelo DISC é amplamente utilizado em contextos organizacionais para:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Recrutamento e seleção de talentos</li>
                    <li>Desenvolvimento de lideranças</li>
                    <li>Formação e gestão de equipes</li>
                    <li>Comunicação interpessoal eficaz</li>
                    <li>Resolução de conflitos</li>
                    <li>Coaching e desenvolvimento profissional</li>
                  </ul>
                  <p>
                    A avaliação consiste em <strong>48 perguntas</strong> em escala Likert (1 a 5), onde cada pergunta 
                    mede características relacionadas aos quatro comportamentos principais. Os resultados são apresentados 
                    em um gráfico que mostra a intensidade de cada perfil (D, I, S, C), identificando o perfil dominante 
                    e secundário do indivíduo.
                  </p>
                  <p className="text-sm italic bg-white p-3 rounded border border-blue-200 mt-3">
                    <strong>Nota:</strong> O DISC não é um teste de personalidade ou capacidade intelectual, mas uma 
                    ferramenta para compreender tendências comportamentais e estilos de comunicação preferidos.
                  </p>
                </div>
              </div>

              {/* Os 4 Perfis DISC */}
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                  Os 4 Perfis Comportamentais DISC
                </h3>

                <div className="space-y-4">
                  {/* D - Dominante */}
                  <Card className="border-2 border-red-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-red-100 rounded-full">
                          <Target className="h-8 w-8 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-red-900 mb-3">
                            D - Dominante (Dominance)
                          </h4>
                          <p className="text-gray-700 mb-3">
                            Pessoas com perfil <strong>Dominante</strong> são diretas, orientadas para resultados e focadas 
                            em objetivos. São decisivas, competitivas e gostam de desafios.
                          </p>
                          <div className="bg-red-50 p-4 rounded-lg mb-3">
                            <p className="font-semibold text-red-900 mb-2">Características principais:</p>
                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                              <li>Confiantes e assertivos em suas decisões</li>
                              <li>Orientados para resultados e conquistas</li>
                              <li>Diretos e objetivos na comunicação</li>
                              <li>Aceitam desafios e correm riscos calculados</li>
                              <li>Focados em eficiência e produtividade</li>
                              <li>Preferem ter controle e autoridade</li>
                            </ul>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                              <p className="font-semibold text-green-900 mb-2 text-sm">✅ Pontos Fortes:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Liderança natural e tomada de decisões rápida</li>
                                <li>• Resiliência diante de desafios</li>
                                <li>• Capacidade de implementar mudanças</li>
                                <li>• Foco em soluções e resultados</li>
                              </ul>
                            </div>
                            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                              <p className="font-semibold text-yellow-900 mb-2 text-sm">⚠️ Pontos de Atenção:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Podem parecer impacientes ou agressivos</li>
                                <li>• Tendência a ignorar detalhes importantes</li>
                                <li>• Dificuldade em delegar controle</li>
                                <li>• Podem ser vistos como insensíveis</li>
                              </ul>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-3 italic">
                            <strong>No trabalho:</strong> Ideais para cargos de liderança, gestão de projetos, vendas 
                            desafiadoras e situações que exigem decisões rápidas. Motivam-se por autonomia e reconhecimento 
                            de conquistas.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* I - Influente */}
                  <Card className="border-2 border-yellow-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-yellow-100 rounded-full">
                          <Users className="h-8 w-8 text-yellow-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-yellow-900 mb-3">
                            I - Influente (Influence)
                          </h4>
                          <p className="text-gray-700 mb-3">
                            Pessoas com perfil <strong>Influente</strong> são comunicativas, entusiastas e orientadas para 
                            relacionamentos. São persuasivas, otimistas e energéticas.
                          </p>
                          <div className="bg-yellow-50 p-4 rounded-lg mb-3">
                            <p className="font-semibold text-yellow-900 mb-2">Características principais:</p>
                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                              <li>Extrovertidos e sociáveis em todas as situações</li>
                              <li>Persuasivos e carismáticos</li>
                              <li>Otimistas e entusiastas</li>
                              <li>Valorizam reconhecimento e aprovação social</li>
                              <li>Excelentes comunicadores verbais</li>
                              <li>Criam conexões e networks facilmente</li>
                            </ul>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                              <p className="font-semibold text-green-900 mb-2 text-sm">✅ Pontos Fortes:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Excelentes habilidades de comunicação</li>
                                <li>• Capacidade de inspirar e motivar</li>
                                <li>• Criação de ambientes positivos</li>
                                <li>• Networking e construção de relacionamentos</li>
                              </ul>
                            </div>
                            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                              <p className="font-semibold text-yellow-900 mb-2 text-sm">⚠️ Pontos de Atenção:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Podem ser desorganizados ou dispersos</li>
                                <li>• Tendência a evitar conflitos necessários</li>
                                <li>• Dificuldade em trabalhos solitários</li>
                                <li>• Podem prometer mais do que conseguem cumprir</li>
                              </ul>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-3 italic">
                            <strong>No trabalho:</strong> Excelentes em vendas, marketing, relações públicas, treinamento 
                            e funções que envolvem contato constante com pessoas. Motivam-se por reconhecimento público 
                            e feedback positivo.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* S - Estável */}
                  <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-green-100 rounded-full">
                          <Heart className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-green-900 mb-3">
                            S - Estável (Steadiness)
                          </h4>
                          <p className="text-gray-700 mb-3">
                            Pessoas com perfil <strong>Estável</strong> são pacientes, leais e orientadas para apoio. 
                            São colaborativas, confiáveis e valorizam harmonia e estabilidade.
                          </p>
                          <div className="bg-green-50 p-4 rounded-lg mb-3">
                            <p className="font-semibold text-green-900 mb-2">Características principais:</p>
                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                              <li>Pacientes e calmos sob pressão</li>
                              <li>Leais e confiáveis</li>
                              <li>Bons ouvintes e empáticos</li>
                              <li>Preferem estabilidade e previsibilidade</li>
                              <li>Trabalham bem em equipe</li>
                              <li>Evitam conflitos e mudanças bruscas</li>
                            </ul>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                              <p className="font-semibold text-green-900 mb-2 text-sm">✅ Pontos Fortes:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Confiáveis e consistentes</li>
                                <li>• Excelentes membros de equipe</li>
                                <li>• Paciência e persistência</li>
                                <li>• Habilidade de criar harmonia</li>
                              </ul>
                            </div>
                            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                              <p className="font-semibold text-yellow-900 mb-2 text-sm">⚠️ Pontos de Atenção:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Resistência a mudanças</li>
                                <li>• Dificuldade em dizer "não"</li>
                                <li>• Podem evitar confrontos necessários</li>
                                <li>• Tendência a manter o status quo</li>
                              </ul>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-3 italic">
                            <strong>No trabalho:</strong> Ideais para atendimento ao cliente, recursos humanos, suporte 
                            técnico, enfermagem e funções que requerem paciência e consistência. Motivam-se por segurança, 
                            reconhecimento da lealdade e ambiente harmonioso.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* C - Consciente */}
                  <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-blue-100 rounded-full">
                          <BarChart3 className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-blue-900 mb-3">
                            C - Consciente (Conscientiousness)
                          </h4>
                          <p className="text-gray-700 mb-3">
                            Pessoas com perfil <strong>Consciente</strong> são analíticas, precisas e orientadas para qualidade. 
                            São sistemáticas, detalhistas e valorizam exatidão e padrões elevados.
                          </p>
                          <div className="bg-blue-50 p-4 rounded-lg mb-3">
                            <p className="font-semibold text-blue-900 mb-2">Características principais:</p>
                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                              <li>Analíticos e orientados por dados</li>
                              <li>Atentos aos detalhes e precisão</li>
                              <li>Sistemáticos e metodológicos</li>
                              <li>Valorizam qualidade e padrões elevados</li>
                              <li>Questionam e verificam informações</li>
                              <li>Preferem seguir regras e procedimentos</li>
                            </ul>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                              <p className="font-semibold text-green-900 mb-2 text-sm">✅ Pontos Fortes:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Precisão e atenção aos detalhes</li>
                                <li>• Pensamento analítico e lógico</li>
                                <li>• Alto padrão de qualidade</li>
                                <li>• Planejamento e organização sistemáticos</li>
                              </ul>
                            </div>
                            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                              <p className="font-semibold text-yellow-900 mb-2 text-sm">⚠️ Pontos de Atenção:</p>
                              <ul className="text-xs text-gray-700 space-y-1">
                                <li>• Podem ser excessivamente críticos</li>
                                <li>• Perfeccionismo pode causar lentidão</li>
                                <li>• Resistência a decisões sem dados</li>
                                <li>• Dificuldade em delegar por desconfiança</li>
                              </ul>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-3 italic">
                            <strong>No trabalho:</strong> Excelentes em contabilidade, engenharia, qualidade, análise de dados, 
                            pesquisa e funções que exigem precisão e rigor. Motivam-se por reconhecimento de expertise e 
                            ambientes estruturados.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Interpretação e Combinações */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  Interpretação dos Resultados DISC
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Importante:</strong> A maioria das pessoas apresenta uma combinação dos quatro perfis DISC, 
                    com 1 ou 2 perfis predominantes (primário e secundário). Essa combinação cria um perfil comportamental único.
                  </p>
                  <div className="bg-white p-4 rounded border border-blue-200">
                    <p className="font-semibold mb-2">Exemplos de Combinações Comuns:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li><strong>DI:</strong> Líderes carismáticos e persuasivos</li>
                      <li><strong>DC:</strong> Executivos focados e exigentes</li>
                      <li><strong>IS:</strong> Conselheiros empáticos e comunicativos</li>
                      <li><strong>SC:</strong> Especialistas confiáveis e detalhistas</li>
                      <li><strong>DS:</strong> Líderes firmes mas pacientes</li>
                      <li><strong>IC:</strong> Comunicadores analíticos</li>
                    </ul>
                  </div>
                  <p>
                    Não existem perfis "melhores" ou "piores" - cada perfil DISC tem seus pontos fortes e contribuições únicas. 
                    O objetivo é conhecer seu perfil para:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Potencializar suas características naturais</li>
                    <li>Desenvolver competências complementares</li>
                    <li>Melhorar a comunicação com diferentes perfis</li>
                    <li>Construir equipes mais equilibradas e produtivas</li>
                    <li>Tomar decisões de carreira mais alinhadas</li>
                  </ul>
                  <p className="text-sm italic bg-blue-100 p-3 rounded mt-3">
                    💡 <strong>Dica:</strong> Use os resultados DISC como ferramenta de autoconhecimento e desenvolvimento, 
                    não como rótulo ou limitação. Os comportamentos podem ser adaptados conforme contexto e necessidade.
                  </p>
                </div>
              </div>

              {/* Referência */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 text-center">
                  <strong>Metodologia baseada em:</strong> "Emotions of Normal People" por William Moulton Marston (1928)
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* History Tab */}
        {allowedTabs.includes('history') && <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Histórico de Avaliações</span>
              </CardTitle>
              <CardDescription>
                Todas as avaliações DISC realizadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assessments.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma avaliação encontrada</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={historyNameFilter}
                        onChange={(e) => setHistoryNameFilter(e.target.value)}
                        placeholder="Buscar por nome do funcionário..."
                        className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <input
                      type="date"
                      value={historyDateFilter}
                      onChange={(e) => setHistoryDateFilter(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {(historyNameFilter || historyDateFilter) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setHistoryNameFilter(''); setHistoryDateFilter(''); }}
                      >
                        Limpar Filtros
                      </Button>
                    )}
                  </div>
                  {filteredHistoryAssessments.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Nenhuma avaliação encontrada para os filtros aplicados</p>
                    </div>
                  ) : (
                <div className="space-y-4">
                  {filteredHistoryAssessments.map(assessment => (
                    <Card key={assessment.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900">
                              {assessment.participant_name}
                            </h4>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>Data: {formatDate(assessment.assessment_date)}</span>
                              {assessment.employee_id && (
                                <Badge variant="outline">Funcionário</Badge>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Perfil Principal:</span>
                              <Badge className={getStyleBadgeColor(assessment.primary_style)}>
                                {getStyleName(assessment.primary_style)}
                              </Badge>
                              {assessment.secondary_style && (
                                <>
                                  <span className="text-sm text-gray-600">Secundário:</span>
                                  <Badge variant="outline" className={getStyleBadgeColor(assessment.secondary_style)}>
                                    {getStyleName(assessment.secondary_style)}
                                  </Badge>
                                </>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setCurrentTab('reports');
                                setCurrentAssessmentId(assessment.id);
                              }}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Relatório
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadReportPDF(assessment.id)}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                            {user?.is_admin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteAssessment(assessment.id, assessment.participant_name)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Excluir
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Reports Tab */}
        {allowedTabs.includes('reports') && <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Relatórios Detalhados</span>
              </CardTitle>
              <CardDescription>
                Análise completa dos perfis DISC
              </CardDescription>
            </CardHeader>
            <CardContent>
              {currentAssessmentId ? (
                <DISCReport assessmentId={currentAssessmentId} onBack={() => setCurrentAssessmentId(null)} />
              ) : assessments.length > 0 ? (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Selecione uma avaliação para ver o relatório detalhado</p>
                  </div>
                  <div className="space-y-2">
                    {assessments.map(assessment => (
                      <Card key={assessment.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setCurrentAssessmentId(assessment.id)}>
                        <CardContent className="py-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-900">{assessment.participant_name}</h4>
                              <p className="text-sm text-gray-600">
                                {formatDate(assessment.assessment_date)} - Perfil: {getStyleName(assessment.primary_style)}
                              </p>
                            </div>
                            <Button variant="outline" size="sm">
                              <FileText className="h-4 w-4 mr-2" />
                              Ver Relatório
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma avaliação encontrada para gerar relatórios</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Public Links Tab */}
        {allowedTabs.includes('public-links') && <TabsContent value="public-links">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Link className="h-5 w-5" />
                <span>Links Públicos</span>
              </CardTitle>
              <CardDescription>
                Crie links para compartilhar avaliações DISC com pessoas externas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PublicLinksManager 
                publicLinks={publicLinks}
                onCreateLink={createPublicLink}
                onDeleteLink={deletePublicLink}
              />
            </CardContent>
          </Card>
        </TabsContent>}
      </Tabs>

      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar avaliação?</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as respostas preenchidas até agora serão perdidas. Deseja realmente cancelar a avaliação DISC?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetAssessment();
                setShowCancelConfirm(false);
              }}
            >
              Sim, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Public Links Manager Component
const PublicLinksManager = ({ publicLinks, onCreateLink, onDeleteLink }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    expires_at: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const linkData = {
        ...formData,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };
      
      await onCreateLink(linkData);
      setFormData({ title: '', description: '', expires_at: '' });
      setShowCreateForm(false);
    } catch (error) {
      // Error handled in parent component
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado para a área de transferência!');
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Create Link Button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="h-4 w-4 mr-2" />
          Criar Novo Link
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg">Criar Link Público</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Título do Link *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: Avaliação DISC - Processo Seletivo"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição opcional que aparecerá na tela pública"
                />
              </div>

              <div>
                <Label htmlFor="expires_at">Data de Expiração (Opcional)</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Se não definida, o link ficará ativo permanentemente
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Criando...' : 'Criar Link'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Links List */}
      {publicLinks.length === 0 ? (
        <div className="text-center py-8">
          <Link className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum link público criado ainda</p>
          <p className="text-sm text-gray-400 mt-2">
            Crie um link para permitir que pessoas externas façam a avaliação DISC
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {publicLinks.map(link => (
            <Card key={link.id} className={`${isExpired(link.expires_at) ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2 flex-grow">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{link.title}</h4>
                      <Badge variant={isExpired(link.expires_at) ? "destructive" : "default"}>
                        {isExpired(link.expires_at) ? 'Expirado' : 'Ativo'}
                      </Badge>
                      {link.external_origin === 'gridrecruter' && (
                        <Badge
                          variant="outline"
                          className="bg-indigo-50 text-indigo-700 border-indigo-300"
                          title={link.external_company_id ? `Empresa externa: ${link.external_company_id}` : 'Criado via API externa'}
                          data-testid={`disc-link-gridrecruter-badge-${link.id}`}
                        >
                          GridRecruter
                        </Badge>
                      )}
                      {link.name && (
                        <span className="text-xs text-gray-500">· {link.name}</span>
                      )}
                    </div>
                    
                    {link.description && (
                      <p className="text-sm text-gray-600">{link.description}</p>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>Criado: {formatDate(link.created_at)}</span>
                      </div>
                      
                      {link.expires_at && (
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>Expira: {formatDate(link.expires_at)}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-1">
                        <Eye className="h-4 w-4" />
                        <span>{link.usage_count || 0} usos</span>
                      </div>
                    </div>

                    <div className="bg-gray-100 rounded p-2 mt-2">
                      <p className="text-xs text-gray-600 mb-1">Link público:</p>
                      <div className="flex items-center space-x-2">
                        <code className="flex-grow text-sm bg-white px-2 py-1 rounded border">
                          {link.public_url}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(link.public_url)}
                          disabled={isExpired(link.expires_at)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDeleteLink(link.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// DISC Report Component
const DISCReport = ({ assessmentId, onBack }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [assessmentId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/api/disc/assessment/${assessmentId}/report`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Carregando relatório...</div>;
  }

  if (!report) {
    return <div className="text-center py-8">Erro ao carregar relatório</div>;
  }

  const chartData = [
    { style: 'D', name: 'Dominante', score: report.d_score, color: '#ef4444' },
    { style: 'I', name: 'Influente', score: report.i_score, color: '#f59e0b' },
    { style: 'S', name: 'Estável', score: report.s_score, color: '#10b981' },
    { style: 'C', name: 'Consciente', score: report.c_score, color: '#3b82f6' }
  ];

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {onBack && (
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={onBack}
          >
            ← Voltar à Lista
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const response = await axios.get(
                  `${API}/api/disc/assessment/${assessmentId}/pdf`,
                  { responseType: 'blob' }
                );
                
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const filename = `DISC_${report.participant_name}_${new Date().toISOString().split('T')[0]}.pdf`;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                
                toast.success('Relatório PDF baixado com sucesso!');
              } catch (error) {
                console.error('Erro ao baixar PDF:', error);
                toast.error('Não foi possível baixar o relatório PDF');
              }
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Baixar PDF
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{report.participant_name}</h2>
            <p className="text-gray-600">Avaliação realizada em {new Date(report.assessment_date).toLocaleDateString('pt-BR')}</p>
            <div className="flex items-center space-x-4 mt-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Perfil Principal:</span>
                <Badge className="bg-blue-100 text-blue-800">
                  {report.primary_style} - {report.primary_style === 'D' ? 'Dominante' : 
                   report.primary_style === 'I' ? 'Influente' :
                   report.primary_style === 'S' ? 'Estável' : 'Consciente'}
                </Badge>
              </div>
              {report.secondary_style && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Secundário:</span>
                  <Badge variant="outline">
                    {report.secondary_style} - {report.secondary_style === 'D' ? 'Dominante' : 
                     report.secondary_style === 'I' ? 'Influente' :
                     report.secondary_style === 'S' ? 'Estável' : 'Consciente'}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scores Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Pontuação DISC</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {chartData.map(item => (
              <div key={item.style} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{item.name} ({item.style})</span>
                  <span className="font-bold">{item.score.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${item.score}%`,
                      backgroundColor: item.color
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Interpretation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Strengths */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-green-700">
              <TrendingUp className="h-5 w-5" />
              <span>Forças Principais</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {report.interpretation.strengths.map((strength, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{strength}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Development Areas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-orange-700">
              <Target className="h-5 w-5" />
              <span>Áreas de Desenvolvimento</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {report.interpretation.development_areas.map((area, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{area}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Communication and Work Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>Estilo de Comunicação</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{report.interpretation.communication_style}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <span>Ambiente de Trabalho Ideal</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{report.interpretation.work_environment}</p>
          </CardContent>
        </Card>
      </div>

      {/* Leadership and Stress */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Estilo de Liderança</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{report.interpretation.leadership_style}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-red-700">Fatores de Estresse</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {report.interpretation.stress_factors.map((factor, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full flex-shrink-0"></div>
                  <span className="text-sm text-gray-700">{factor}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-blue-700">
            <FileText className="h-5 w-5" />
            <span>Recomendações de Desenvolvimento</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {report.interpretation.recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <span className="text-gray-700">{recommendation}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default DISCAssessmentPage;